"use client";

import { useState, useRef, useEffect, useMemo } from "react";
import { useTranslations } from "next-intl";
import { ChevronDown, Search } from "lucide-react";
import { LinkWithChannel } from "@/ui/atoms/link-with-channel";
import {
	CUSTOM_PEPTIDE_SLUG,
	PEPTIDE_DATA,
	getPeptideCategoryGroups,
	getPeptideName,
	getPeptideCategory,
} from "@/lib/calculator/peptide-data";
import type { Locale } from "@/config/locale";

interface PeptideSelectorProps {
	selectedSlug: string;
	onChange: (slug: string) => void;
	locale: string;
	disabled?: boolean;
}

export function PeptideSelector({ selectedSlug, onChange, locale, disabled }: PeptideSelectorProps) {
	const t = useTranslations("peptideCalculator");
	const [open, setOpen] = useState(false);
	const [search, setSearch] = useState("");
	const containerRef = useRef<HTMLDivElement>(null);

	const selectedPeptide = PEPTIDE_DATA.find((p) => p.slug === selectedSlug);

	const categoryGroups = useMemo(() => getPeptideCategoryGroups(locale as Locale), [locale]);
	const customPeptide = useMemo(() => PEPTIDE_DATA.find((p) => p.slug === CUSTOM_PEPTIDE_SLUG), []);

	// Close on click outside
	useEffect(() => {
		function handleClickOutside(event: MouseEvent) {
			if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
				setOpen(false);
			}
		}
		document.addEventListener("mousedown", handleClickOutside);
		return () => document.removeEventListener("mousedown", handleClickOutside);
	}, []);

	const query = search.toLowerCase();
	const categoriesWithPeptides = categoryGroups
		.map((group) => ({
			...group,
			peptides: search.trim()
				? group.peptides.filter((peptide) => {
						const name = getPeptideName(peptide, locale as Locale);
						const category = getPeptideCategory(peptide, locale as Locale);
						return name.toLowerCase().includes(query) || category.toLowerCase().includes(query);
					})
				: group.peptides,
		}))
		.filter((group) => group.peptides.length > 0);

	if (disabled && selectedPeptide) {
		return (
			<div className="space-y-2">
				<div className="flex flex-wrap items-center gap-2 rounded-md border border-input bg-secondary px-3 py-2">
					<span className="text-sm font-medium text-foreground">
						{getPeptideName(selectedPeptide, locale as Locale)}
					</span>
					<span className="text-xs text-muted-foreground">
						{getPeptideCategory(selectedPeptide, locale as Locale)}
					</span>
				</div>
				<p className="text-xs text-muted-foreground">{t("form.peptideLockedHint")}</p>
				<LinkWithChannel
					href="/peptide-calculator"
					className="text-xs font-medium text-primary underline-offset-4 hover:underline"
				>
					{t("form.peptideChangeLink")}
				</LinkWithChannel>
			</div>
		);
	}

	return (
		<div className="relative" ref={containerRef}>
			<button
				type="button"
				onClick={() => setOpen((prev) => !prev)}
				className="flex h-10 w-full items-center justify-between rounded-md border border-input bg-background px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
				aria-haspopup="listbox"
				aria-expanded={open}
			>
				<span className="text-foreground">
					{selectedPeptide ? getPeptideName(selectedPeptide, locale as Locale) : t("selector.placeholder")}
				</span>
				<ChevronDown
					className={`h-4 w-4 text-muted-foreground transition-transform ${open ? "rotate-180" : ""}`}
				/>
			</button>

			{open && (
				<div className="absolute z-50 mt-1 w-full rounded-md border border-border bg-background shadow-lg">
					{/* Search input */}
					<div className="flex items-center gap-2 border-b border-border px-3 py-2">
						<Search className="h-4 w-4 shrink-0 text-muted-foreground" />
						<input
							autoFocus
							type="text"
							value={search}
							onChange={(e) => setSearch(e.target.value)}
							placeholder={t("selector.search")}
							className="flex-1 bg-transparent text-sm outline-none placeholder:text-muted-foreground"
						/>
					</div>

					{/* Grouped list */}
					<div className="max-h-64 overflow-y-auto py-1">
						{customPeptide && (
							<div className="border-b border-border px-1 pb-1">
								<div className="px-2 py-1 text-xs font-semibold text-muted-foreground">
									{t("selector.other")}
								</div>
								<button
									type="button"
									role="option"
									aria-selected={customPeptide.slug === selectedSlug}
									onClick={() => {
										onChange(customPeptide.slug);
										setOpen(false);
										setSearch("");
									}}
									className={`flex w-full items-center gap-2 rounded-md px-3 py-1.5 text-sm transition-colors hover:bg-secondary ${
										customPeptide.slug === selectedSlug ? "bg-primary/10 text-primary" : "text-foreground"
									}`}
								>
									{getPeptideName(customPeptide, locale as Locale)}
								</button>
							</div>
						)}
						{search.trim() && categoriesWithPeptides.length === 0 && (
							<div className="px-3 py-2 text-sm text-muted-foreground">{t("selector.noFurther")}</div>
						)}
						{categoriesWithPeptides.map((cat) => {
							const peptides = cat.peptides;
							return (
								<div key={cat.key}>
									<div className="px-3 py-1 text-xs font-semibold text-muted-foreground">{cat.label}</div>
									{peptides.map((peptide) => (
										<button
											key={peptide.slug}
											type="button"
											role="option"
											aria-selected={peptide.slug === selectedSlug}
											onClick={() => {
												onChange(peptide.slug);
												setOpen(false);
												setSearch("");
											}}
											className={`flex w-full items-center gap-2 px-4 py-1.5 text-sm transition-colors hover:bg-secondary ${
												peptide.slug === selectedSlug ? "bg-primary/10 text-primary" : "text-foreground"
											}`}
										>
											{getPeptideName(peptide, locale as Locale)}
										</button>
									))}
								</div>
							);
						})}
					</div>
				</div>
			)}
		</div>
	);
}
